/**
 * Profile Service
 *
 * API client for customer profile endpoints.
 * Profile contains:
 * - Encrypted data: Personal info, contact, beauty profile (READ-ONLY)
 * - Non-encrypted data: Communication preferences (READ/WRITE)
 */

import type { EncryptedRecord } from './customerData.service'
import type { UpdatePreferencesRequest, CommunicationPreferences } from '@/types/profile'
import type { ProfileApiResponse, ApiCommunicationPreferences } from '@/utils/profileTransform'
import { get, put } from './api.service'

// Re-export for convenience
export type { ProfileApiResponse }

/**
 * Response from preference update
 */
export interface UpdatePreferencesResponse {
  success: boolean
  preferences?: ApiCommunicationPreferences
  message?: string
}

/**
 * Full profile fetch result
 * Contains both the encrypted record for decryption and the API response for metadata
 */
export interface ProfileFetchResult {
  encryptedRecord: EncryptedRecord
  apiResponse: ProfileApiResponse
}

/**
 * Fetch customer profile
 *
 * Returns encrypted profile record that must be decrypted client-side,
 * along with the full API response for accessing non-encrypted fields.
 *
 * @returns Profile fetch result or null if not found
 */
export async function fetchProfile(): Promise<ProfileFetchResult | null> {
  try {
    const response = await get<ProfileApiResponse>('/profile')

    if (!response) return null

    // DEBUG: Log encrypted data details for decryption troubleshooting
    console.debug('[Profile] Raw API response:', {
      customerId: response.customerId,
      ciphertextLength: response.ciphertext?.length || 0,
      ciphertextPreview: response.ciphertext?.substring(0, 50) + '...',
      ivLength: response.iv ? atob(response.iv).length : 0,
      ivBase64: response.iv,
      hasAuthTag: !!response.authTag,
      authTagLength: response.authTag ? atob(response.authTag).length : 0,
      version: response.version,
      communicationPreferences: response.communicationPreferences,
    })

    // Transform flat API response to EncryptedRecord structure
    const encryptedRecord: EncryptedRecord = {
      id: response.customerId,
      encryptedData: {
        ciphertext: response.ciphertext,
        iv: response.iv,
        authTag: response.authTag || '',
      },
      metadata: {
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
        version: response.version,
      },
    }

    return {
      encryptedRecord,
      apiResponse: response,
    }
  } catch (error) {
    // Return null for 404 errors
    if (error instanceof Error && 'statusCode' in error && (error as { statusCode: number }).statusCode === 404) {
      return null
    }
    throw error
  }
}

/**
 * Update customer communication preferences
 *
 * Only preferences can be updated by customers.
 * All other profile data is encrypted and read-only.
 *
 * @param preferences - Preference fields to update
 * @returns Response with updated preferences
 */
export async function updatePreferences(
  preferences: UpdatePreferencesRequest
): Promise<UpdatePreferencesResponse> {
  return put<UpdatePreferencesResponse>('/profile/preferences', preferences)
}

/**
 * Fetch communication preferences only
 *
 * Returns just the preferences without the encrypted profile data.
 * Useful when you only need to display/edit preferences.
 *
 * @returns Current communication preferences
 */
export async function fetchPreferences(): Promise<CommunicationPreferences | null> {
  try {
    return await get<CommunicationPreferences>('/profile/preferences')
  } catch (error) {
    // Return null for 404 errors
    if (error instanceof Error && 'statusCode' in error && (error as { statusCode: number }).statusCode === 404) {
      return null
    }
    throw error
  }
}

// Export as named service object
export const profileService = {
  fetchProfile,
  updatePreferences,
  fetchPreferences,
}

export default profileService
